import React, { useMemo, useState } from "react";
import { useEOBQuery } from "@/hooks/use-care-history";
import { queryClient } from "@/hooks/use-query-client";
import { LoaderCircle, X } from "lucide-react";

import {
  CLAIM_TYPES,
  CLAIMS_STATUS,
  EOB_VERBIAGE,
  GROUP_ID,
  MIME_TYPE,
  QueryKeys,
  VISIT_DETAILS_CLAIM_TYPE,
} from "@/constants";
import {
  formatToCurrency,
  hasNumberInString,
  normalizeClaimText,
  openFileFromBase64,
  openPendingWindow,
} from "@/lib/utils";
import type { CareHistoryClaimsResponseType } from "@/model/care-history";
import type { Relation } from "@/model/demographics";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatDateToLabel } from "@/lib/date-utils";
import useUserInfo from "@/hooks/use-user-info";
import AppDialog from "../app-dialog/app-dialog";
import ClaimStatusBadge from "./claim-status-badge";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import IDENTIFIERS from "@/constants/test_id";
import type { LogContext } from "@/services/logger/logger-types";
import HccbWrapper from "../home/feature-list-item-hcbb";
import {
  CustomLogTitle,
  LogCategories,
  LogEventTypeEnum,
  LogSubCategories,
  LogTags,
  PANEL,
} from "@/services/logger/logger-types";
import { useActionLogger } from "@/services/logger/logger-hooks";
import { capitalizeInitialLetter } from "@/lib/string-util";
import { THIRD_PARTY_INTEGRATION } from "@/lib/client-utils";

type VisitDetailsModalProps = {
  isOpen: boolean;
  visitDetail: CareHistoryClaimsResponseType;
  appealClaimUrl?: string;
  onClose: () => void;
  onEobError?: () => void;
};

const EM_DASH = "—";
const SERVICE_TYPE = "Service Type";
const DRUG_NAME = "Drug Name";
const PENDING_COST_NOTE =
  "Costs and your EOB will appear once processing completes.";
const PENDING_EOB_TOOLTIP = "Available after processing completes.";

/** A single row of the cost breakdown list. `dot` is null when the row has no bar segment. */
type BreakdownRow = {
  label: string;
  value: string | null;
  dot: string | null;
  muted: boolean;
  isCredit?: boolean;
};

/** Parse a currency-ish string to a finite number, or null when not numeric. */
const toNumber = (value?: string): number | null => {
  if (!value || !hasNumberInString(value)) return null;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Format an amount for the cost block. Returns null (rendered as an em-dash) when the value
 * is Pending or non-numeric, so the "Processing" state shows dashes instead of NaN.
 */
const formatAmount = (value?: string): string | null => {
  if (!value || value === CLAIMS_STATUS.PENDING) return null;
  return hasNumberInString(value)
    ? formatToCurrency(parseFloat(value))
    : normalizeClaimText(value);
};

const VisitDetailsModal: React.FunctionComponent<VisitDetailsModalProps> = ({
  isOpen,
  visitDetail,
  appealClaimUrl,
  onClose,
  onEobError,
}) => {
  const { logAction } = useActionLogger();
  const { activeCoverage } = useSelector(
    (state: RootState) => state?.planCoverageInfo,
  );
  const userInfo = useUserInfo();
  const members = (queryClient.getQueryData([QueryKeys.MEMBERS]) ??
    {}) as Record<string, Relation>;
  const { isFetching: isEOBLoading, refetch } = useEOBQuery(
    visitDetail?.revClaimId,
    visitDetail?.memberId,
    activeCoverage?.claimCoverageCode,
    activeCoverage?.planId,
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  const logContext = useMemo(
    () => ({
      claimId: visitDetail?.revClaimId,
      memberId: visitDetail?.memberId,
      activeCoverage: activeCoverage?.claimCoverageCode,
      planId: activeCoverage?.planId,
    }),
    [visitDetail?.revClaimId, visitDetail?.memberId, activeCoverage],
  );

  // logMetadata carried on the EOB action + button interactions (mirrors visit-details.tsx).
  const eobLogContext = {
    claimNumber: visitDetail?.revClaimId,
    memberId: visitDetail?.memberId,
    claimCoverageCode: activeCoverage?.claimCoverageCode,
    planId: activeCoverage?.planId,
  };

  const fetchEOB = async (logMetaData: Record<string, unknown>) => {
    const cachedEOB = queryClient.getQueryData<string>([
      QueryKeys.EOB,
      visitDetail?.revClaimId,
    ]);

    const pendingWindow =
      !cachedEOB || cachedEOB.length > 10000 ? openPendingWindow() : null;
    if (!cachedEOB) {
      const { data: eobData, error } = await refetch();
      onClose?.();
      if (error || !eobData) {
        pendingWindow?.close();
        onEobError?.();
      } else {
        await openEOB(eobData, logMetaData, pendingWindow);
      }
    } else {
      await openEOB(cachedEOB, logMetaData, pendingWindow);
      onClose?.();
    }
  };

  const openEOB = async (
    eob: string,
    logMetaData: Record<string, unknown>,
    pendingWindow: Window | null,
  ) => {
    const logOptions: LogContext = {
      tag: LogTags.MODULE_ACCESS,
      category: undefined,
      subCategory: undefined,
      type: LogEventTypeEnum.PAGE_VIEW,
    };
    if (eob?.length > 10000) {
      logAction(
        capitalizeInitialLetter(CustomLogTitle.EOB_CARD),
        logMetaData,
        logOptions,
      );
      const isOpened = await openFileFromBase64(
        eob,
        MIME_TYPE.PDF,
        pendingWindow,
      );
      if (!isOpened) {
        pendingWindow?.close();
        onEobError?.();
      }
    } else {
      pendingWindow?.close();
      setDialogOpen(true);
    }
  };

  const isPharmacy = visitDetail?.claimType === CLAIM_TYPES.PHARMACY.VALUE;
  const providerLabel = isPharmacy
    ? VISIT_DETAILS_CLAIM_TYPE.PHARMACY
    : VISIT_DETAILS_CLAIM_TYPE.MEDICAL;
  const paidDateLabel = userInfo?.groupId?.startsWith("COBALT")
    ? "Paid"
    : "Processing";

  const patient = members[visitDetail?.memberId];
  const patientName = `${patient?.firstName} ${patient?.lastName}`;
  const serviceType = isPharmacy
    ? visitDetail?.drugName
    : visitDetail?.pointOfService;
  const claimStatus = visitDetail?.revAdjudicationDesc;
  const claimStatusDefinition = visitDetail?.statusDefinition;

  const providerAddress =
    userInfo?.groupId === GROUP_ID.SOMEHA
      ? "Not Available"
      : visitDetail?.prvAddress1 !== null && visitDetail?.prvAddress1 !== ""
        ? visitDetail?.prvAddress1
        : "Not Available";

  // ---- Cost block --------------------------------------------------------
  const planPaidNum = toNumber(visitDetail?.planPaid);
  const oopNum = toNumber(visitDetail?.totalOopPaid);
  // The bar apportions what the plan settled on — the plan's share plus the member's.
  // The billed amount is reported as its own row and never sizes the bar.
  const coveredTotal =
    planPaidNum !== null && oopNum !== null ? planPaidNum + oopNum : null;

  const oopDisplay = formatAmount(visitDetail?.totalOopPaid);
  const billedDisplay = formatAmount(visitDetail?.billedAmt);
  const isReversed = claimStatus === CLAIMS_STATUS.REVERSED;
  // A reversed claim backs out the original one, so it always reads as a credit.
  const isCredit = isReversed || (oopNum !== null && oopNum < 0);
  // Still processing: no final responsibility yet, so the cost tiles would only show
  // dashes. Swap them for a note that says the amounts are coming.
  const isPending = !isReversed && oopDisplay === null;

  const showBar =
    !isReversed &&
    planPaidNum !== null &&
    oopNum !== null &&
    planPaidNum >= 0 &&
    oopNum >= 0 &&
    coveredTotal !== null &&
    coveredTotal > 0;

  const oopAmount = (
    <span className="text-xl font-bold tracking-tight text-foreground">
      {oopDisplay}
    </span>
  );

  const heroCaption = (): React.ReactNode => {
    if (oopDisplay === null) {
      return (
        <>
          <span className="text-xl font-bold tracking-tight text-muted-foreground">
            {`$ ${EM_DASH}`}
          </span>{" "}
          final amount not yet available
        </>
      );
    }
    if (isCredit) return <>{oopAmount} credited back to you</>;
    if (!billedDisplay) return <>{oopAmount} you owe</>;
    return (
      <>
        you owe {oopAmount} of{" "}
        <span className="font-semibold text-foreground">
          {billedDisplay} billed
        </span>
      </>
    );
  };

  // A reversed claim backs out the original, so there is nothing to apportion — the rows drop
  // their dots and describe what was backed out instead.
  const breakdown: BreakdownRow[] = isReversed
    ? [
        {
          label: "Total charge reversed",
          value: billedDisplay,
          dot: null,
          muted: true,
        },
        {
          label: "Plan payment reversed",
          value: formatAmount(visitDetail?.planPaid),
          dot: null,
          muted: true,
        },
        {
          label: "Your responsibility",
          value: oopDisplay,
          dot: null,
          muted: false,
          isCredit: true,
        },
      ]
    : [
        {
          label: "Total charge",
          value: billedDisplay,
          dot: null,
          muted: true,
        },
        {
          label: "Paid by plan",
          value: formatAmount(visitDetail?.planPaid),
          dot: "bg-primary",
          muted: true,
        },
        {
          label: "Your responsibility",
          value: oopDisplay,
          dot: "bg-foreground",
          muted: false,
        },
      ];

  const tiles = [
    { label: "Deductible", value: visitDetail?.deductibleAmt },
    { label: "Copay", value: visitDetail?.copayAmt },
    { label: "Coinsurance", value: visitDetail?.coinsuranceAmt },
  ];

  const denialReason = visitDetail?.denialReason
    ? { label: "Denial Reason", value: visitDetail.denialReason }
    : null;

  const details = [
    { label: `${providerLabel} Name`, value: visitDetail?.prvFullName },
    {
      label: isPharmacy ? DRUG_NAME : SERVICE_TYPE,
      value: serviceType,
    },
    ...(visitDetail?.prvAddress1
      ? [{ label: `${providerLabel} Address`, value: providerAddress }]
      : []),
    ...(visitDetail?.revClaimId
      ? [{ label: "Claim Number", value: visitDetail?.revClaimId }]
      : []),
    {
      label: isReversed ? "Reversed on" : `${paidDateLabel} Date`,
      value: visitDetail?.revPaidDate,
    },
  ];

  const showAppealClaim =
    !isPharmacy &&
    !!appealClaimUrl &&
    appealClaimUrl.length > 0 &&
    claimStatus === CLAIMS_STATUS.PAID;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      logDialogName={PANEL.VISIT_DETAILS}
      logType={LogEventTypeEnum.PAGE_VIEW}
      logTag={LogTags.MODULE_ACCESS}
      logCategory={LogCategories.CARE_AND_CLAIMS_HISTORY}
      logSubCategory={LogSubCategories.CARE_HISTORY_VISIT_DETAILS}
      logMetadata={logContext}
    >
      <DialogContent
        className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[680px]"
        logDialogName={PANEL.VISIT_DETAILS}
        logCategory={LogCategories.CARE_AND_CLAIMS_HISTORY}
        hideCloseButton
        // Don't auto-focus a control on open — avoids a stray focus ring on the close/action buttons.
        onOpenAutoFocus={(event) => event.preventDefault()}
        data-testid={IDENTIFIERS.CARE_AND_CLAIMS_HISTORY.VISIT_DETAILS}
      >
        {/* Header — fixed, never scrolls */}
        <div className="flex flex-none items-center gap-3 border-b border-border px-7 py-4">
          <div className="min-w-0 flex-1">
            <DialogTitle className="truncate text-normal font-bold tracking-tight">
              {patientName}
            </DialogTitle>
            <DialogDescription className="mt-0.5 truncate text-medium text-muted-foreground">
              {serviceType ? `${serviceType} · ` : ""}
              {formatDateToLabel(visitDetail?.recentSvcDate)}
            </DialogDescription>
          </div>
          {claimStatus ? (
            <div className="flex-none">
              <ClaimStatusBadge
                revAdjudicationDesc={claimStatus}
                statusDefinition={claimStatusDefinition}
              />
            </div>
          ) : null}
          <DialogClose
            asChild
            logDialogName={PANEL.VISIT_DETAILS}
            logCategory={LogCategories.CARE_AND_CLAIMS_HISTORY}
          >
            <button
              type="button"
              aria-label="Close"
              className="flex size-8 flex-none items-center justify-center rounded-sm text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-5" />
            </button>
          </DialogClose>
        </div>

        {/* Body — the only scrollable region */}
        <div className="flex-1 overflow-y-auto">
          {/* Cost block */}
          <div className="border-b border-border px-7 py-6">
            <div className="flex items-baseline gap-2">
              <span className="text-medium">{heroCaption()}</span>
            </div>

            {/* No bar for a reversed claim — there is nothing to apportion. */}
            {!isReversed ? (
              <div
                className="mt-3.5 flex h-2 overflow-hidden rounded bg-muted"
                aria-hidden="true"
              >
                {showBar ? (
                  <>
                    <div
                      className="mr-0.5 bg-primary"
                      style={{ flexGrow: planPaidNum ?? 0 }}
                    />
                    <div
                      className="bg-foreground"
                      style={{ flexGrow: oopNum ?? 0 }}
                    />
                  </>
                ) : null}
              </div>
            ) : null}

            <div className="mt-4 flex flex-col gap-2">
              {breakdown.map((row) => (
                <div
                  key={row.label}
                  className="flex items-center gap-2 text-medium"
                >
                  {row.dot ? (
                    <span
                      className={`size-2 flex-none rounded-full ${row.dot}`}
                    />
                  ) : null}
                  <span
                    className={`flex-1 ${
                      row.muted
                        ? "text-muted-foreground"
                        : "font-semibold text-foreground"
                    }`}
                  >
                    {row.label}
                  </span>
                  <span
                    className={`${row.muted ? "font-semibold" : "font-bold"}`}
                  >
                    {row.value ?? EM_DASH}
                  </span>
                </div>
              ))}
            </div>

            {isPending ? (
              <p className="mt-3 text-sm text-[#dd0000]">{PENDING_COST_NOTE}</p>
            ) : (
              <div className="mt-3.5 flex gap-2">
                {tiles.map((tile) => (
                  <div
                    key={tile.label}
                    className="flex-1 rounded-md border border-border bg-muted px-3 py-2.5"
                  >
                    <div className="text-sm font-semibold text-muted-foreground">
                      {tile.label}
                    </div>
                    <div className="mt-0.5 text-medium font-bold">
                      {hasNumberInString(tile.value)
                        ? formatToCurrency(parseFloat(tile.value))
                        : normalizeClaimText(tile.value)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isReversed ? (
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                This claim was reversed
                {visitDetail?.revPaidDate ? (
                  <>
                    {" on "}
                    <span className="font-semibold text-foreground">
                      {visitDetail.revPaidDate}
                    </span>
                  </>
                ) : null}{" "}
                {EM_DASH} these entries back out the original claim. Nothing is
                owed, and anything you&apos;d paid is credited back.
              </p>
            ) : null}

            {denialReason ? (
              <div className="mt-3 text-sm">
                <p className="font-semibold text-foreground">
                  {denialReason.label}
                </p>
                <p className="mt-0.5 text-justify text-muted-foreground">
                  {denialReason.value}
                </p>
              </div>
            ) : null}
          </div>

          {/* Details list */}
          <dl className="px-7 py-2">
            {details.map((row, idx) => (
              <div
                key={row.label}
                className={`flex justify-between gap-4 py-2.5 text-medium ${
                  idx < details.length - 1 ? "border-b border-border" : ""
                }`}
              >
                <dt className="text-muted-foreground">{row.label}</dt>
                <dd className="text-right font-semibold">
                  {row.value || EM_DASH}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Footer actions — fixed */}
        <div className="flex flex-none flex-col gap-2 border-t border-border px-7 py-4 sm:flex-row sm:justify-end">
          {visitDetail?.isPayOutOfFlex && (
            <HccbWrapper
              feature={{ action: { sso_id: THIRD_PARTY_INTEGRATION.WEX } }}
            >
              {({ onClick, isPending }) => (
                <Button
                  variant="default"
                  className="rounded-sm hover:bg-primary-selected"
                  onClick={onClick}
                  disabled={isPending}
                  logMetadata={eobLogContext}
                  data-testid={
                    IDENTIFIERS.CARE_AND_CLAIMS_HISTORY.PAYOUT_OF_FLEX_BUTTON
                  }
                >
                  {isPending ? (
                    <LoaderCircle className="size-6 animate-spin" />
                  ) : (
                    "Pay Out of Flex"
                  )}
                </Button>
              )}
            </HccbWrapper>
          )}

          {showAppealClaim ? (
            <Button
              variant="default"
              className="rounded-sm hover:bg-primary-selected"
              onClick={() => window.open(appealClaimUrl, "_blank")}
              logMetadata={eobLogContext}
              data-testid={
                IDENTIFIERS.CARE_AND_CLAIMS_HISTORY.APPEAL_CLAIM_BUTTON
              }
            >
              Appeal Claim
            </Button>
          ) : null}

          {isPending ? (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* A disabled button swallows pointer events, so the wrapper is the hover target. */}
                <span tabIndex={0} className="inline-flex w-full sm:w-auto">
                  <Button
                    variant="default"
                    className="w-full rounded-sm sm:w-auto"
                    disabled
                    data-testid={
                      IDENTIFIERS.CARE_AND_CLAIMS_HISTORY.VIEW_EOB_BUTTON
                    }
                  >
                    View EOB
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{PENDING_EOB_TOOLTIP}</p>
              </TooltipContent>
            </Tooltip>
          ) : visitDetail?.showEob ? (
            <Button
              variant="default"
              className="rounded-sm hover:bg-primary-selected"
              onClick={() => fetchEOB(eobLogContext)}
              logMetadata={eobLogContext}
              data-testid={IDENTIFIERS.CARE_AND_CLAIMS_HISTORY.VIEW_EOB_BUTTON}
            >
              {isEOBLoading ? (
                <LoaderCircle className="size-6 animate-spin" />
              ) : (
                "View EOB"
              )}
            </Button>
          ) : null}
        </div>
      </DialogContent>

      <AppDialog
        open={dialogOpen}
        message={EOB_VERBIAGE.EOB_PENDING}
        onOpenChange={setDialogOpen}
      />
    </Dialog>
  );
};

export default VisitDetailsModal;
